import { zodResolver } from '@hookform/resolvers/zod';
import { type DefaultValues, type FieldValues, type UseFormProps, type UseFormReturn, useForm } from 'react-hook-form';
import type { z } from 'zod';

/** A zod object schema whose input and output are form values. */
export type FormSchema = z.ZodType<FieldValues, FieldValues>;

export type ZodFormReturn<TSchema extends FormSchema> = UseFormReturn<z.input<TSchema>, unknown, z.output<TSchema>>;

export interface UseZodFormOptions<TSchema extends FormSchema>
  extends Omit<UseFormProps<z.input<TSchema>, unknown, z.output<TSchema>>, 'resolver' | 'defaultValues'> {
  /**
   * Every field's starting value. Required: a field without one is uncontrolled until it is touched,
   * and server errors can only be placed on fields the form holds.
   */
  defaultValues: DefaultValues<z.input<TSchema>>;
}

/**
 * The one way to start a form. `schema` is the GENERATED request schema from
 * `client/src/generated/api/zod.gen.ts` (e.g. `zCreateWorkflowBody`), so the client validates exactly
 * what the server will. Extend it only with UI-only fields (`zX.extend({ confirmPassword })`).
 *
 * Validation runs when a field is left (`onTouched`), then on every change of a field that has an error.
 */
export function useZodForm<TSchema extends FormSchema>(
  schema: TSchema,
  options: UseZodFormOptions<TSchema>,
): ZodFormReturn<TSchema> {
  return useForm<z.input<TSchema>, unknown, z.output<TSchema>>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    ...options,
    // @hookform/resolvers 7 matches a zod 4 schema by its `_zod` input and output, which it cannot infer from a
    // schema that is still a type parameter: they are named here.
    resolver: zodResolver<TSchema['_zod']['input'], unknown, TSchema['_zod']['output'], TSchema>(schema),
  });
}
